import React, { useState } from 'react'

import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'

import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/client/src/shared/components/shadcn-ui/dialog'
import Localization from '~/client/src/shared/localization/LocalizationManager'

const FLAT_ROWS: string[][] = [
  ['Option A', '', ''],
  ['Option B', '', ''],
  ['Option C', '', ''],
  ['Option D', '', ''],
  ['Option E', '', ''],
  ['Option F', '', ''],
  ['Option G', '', ''],
]

const METHOD_1_ROWS: string[][] = [
  ['Parent A', '', ''],
  ['', 'Child A1', ''],
  ['', 'Child A2', ''],
  ['Parent B', '', ''],
  ['', 'Child B1', ''],
  ['', '', 'Child B1.1'],
  ['', '', 'Child B1.2'],
]

const METHOD_2_ROWS: string[][] = [
  ['Parent A', '', ''],
  ['Parent A', 'Child A1', ''],
  ['Parent A', 'Child A2', ''],
  ['Parent B', '', ''],
  ['Parent B', 'Child B1', ''],
  ['Parent B', 'Child B1', 'Child B1.1'],
  ['Parent B', 'Child B1', 'Child B1.2'],
]

const SlideGrid: React.FC<{ rows: string[][] }> = ({ rows }) => (
  <table className="tw:w-full tw:table-fixed tw:border-collapse tw:bg-background">
    <tbody>
      {rows.map((row, rowIndex) => (
        <tr key={rowIndex}>
          {row.map((cell, cellIndex) => (
            <td
              key={cellIndex}
              className="tw:w-1/3 tw:border tw:border-border tw:px-2 tw:py-1 tw:text-app-dense tw:text-foreground"
            >
              {cell}
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
)

interface IBulkImportGuideProps {
  onBack: () => void
}

const BulkImportGuide: React.FC<IBulkImportGuideProps> = ({ onBack }) => {
  const [index, setIndex] = useState(0)
  const translator = Localization.translator

  const slides = [
    {
      heading: translator.flatDatasets,
      description: translator.flatDatasetsHint,
      rows: FLAT_ROWS,
    },
    {
      heading: translator.hierarchicalDatasetMethod1,
      description: translator.hierarchicalDatasetMethod1Hint,
      rows: METHOD_1_ROWS,
    },
    {
      heading: translator.hierarchicalDatasetMethod2,
      description: translator.hierarchicalDatasetMethod2Hint,
      rows: METHOD_2_ROWS,
    },
  ]
  const slide = slides[index]
  const isFirst = index === 0
  const isLast = index === slides.length - 1

  return (
    <>
      <DialogHeader>
        <DialogTitle>{translator.copyingAndPastingFromExcel}</DialogTitle>
        <DialogDescription>
          {translator.bulkImportGuideSubtitle}
        </DialogDescription>
      </DialogHeader>

      <div className="tw:flex tw:flex-col tw:gap-3">
        <div className="tw:flex tw:items-center tw:gap-4">
          <Button
            variant="outline"
            size="icon"
            className="tw:shrink-0 tw:rounded-full"
            onClick={() => setIndex(current => current - 1)}
            disabled={isFirst}
            aria-label={translator.previous}
          >
            <ChevronLeftIcon />
          </Button>

          <div
            role="region"
            aria-live="polite"
            aria-label={translator.tipXOfY(index + 1, slides.length)}
            className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-3 tw:rounded-xl tw:border tw:border-border tw:bg-muted tw:p-4"
          >
            <div className="tw:flex tw:flex-col tw:gap-1">
              <span className="tw:text-app-dense-medium tw:text-foreground">
                {slide.heading}
              </span>
              <span className="tw:text-app-dense tw:text-muted-foreground">
                {slide.description}
              </span>
            </div>
            <SlideGrid rows={slide.rows} />
          </div>

          <Button
            variant="outline"
            size="icon"
            className="tw:shrink-0 tw:rounded-full"
            onClick={() => setIndex(current => current + 1)}
            disabled={isLast}
            aria-label={translator.next}
          >
            <ChevronRightIcon />
          </Button>
        </div>

        <div className="tw:flex tw:justify-center">
          <span className="tw:text-app-micro tw:text-muted-foreground">
            {index + 1}/{slides.length}
          </span>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onBack}>
          {translator.back}
        </Button>
      </DialogFooter>
    </>
  )
}

export default BulkImportGuide
